import Box, { type BoxProps } from '@mui/material/Box';
import { useMemo } from 'react';

import {
  RIBBON_WAVE,
  RIBBON_WAVE_STEP_MS,
  ribbonWaveCycleMs,
  ribbonWaveKeyframes,
} from './ribbonMotion';
import { VisuallyHidden } from './VisuallyHidden';

type Props = Omit<BoxProps, 'children'> & {
  text: string;
  /** Hold still — the wave is decorative and a caller may not want it yet. */
  paused?: boolean;
};

/**
 * Sends a ripple through a line of text, one character at a time, on a loop.
 *
 * **The accessibility problem this solves.** Animating characters means
 * putting each one in its own element, and a word split into forty elements
 * is not reliably a word any more: several screen readers announce the pieces
 * individually, so "Import" becomes "I M P O R T". Chopping text up is also
 * what breaks find-in-page and text selection.
 *
 * So the split version is `aria-hidden` and purely decorative, and the real
 * sentence is supplied once beside it in a `VisuallyHidden`. Assistive
 * technology reads one ordinary string; the screen shows forty moving pieces.
 * They are always the same `text` prop, so the two cannot drift.
 *
 * Spaces become non-breaking spaces because an inline-block span containing a
 * single ordinary space collapses to nothing, which would close up every gap
 * in the sentence. The line is laid out as a flex row for the same reason —
 * it keeps the characters on one baseline without relying on whitespace in
 * the markup.
 */
export function WaveText({ text, paused = false, sx, ...rest }: Props) {
  const characters = useMemo(() => [...text], [text]);
  // Derived from the length so the ripple runs without a gap; see ribbonMotion.
  const cycleMs = ribbonWaveCycleMs(characters.length);

  return (
    <>
      <Box
        aria-hidden
        sx={[
          {
            display: 'inline-flex',
            whiteSpace: 'pre',
            [`@keyframes ${RIBBON_WAVE}`]: ribbonWaveKeyframes,
            '& > span': {
              display: 'inline-block',
              // No `will-change`: see ribbonMotion.ts. Promoting a layer per
              // character for the whole session is the one way this gets
              // expensive, and the browser handles it well enough on its own.
              animation: !paused ? `${RIBBON_WAVE} ${cycleMs}ms linear infinite` : 'none',
            },
            // The wave is decoration, so it stops entirely rather than being
            // slowed down. The sentence is still fully legible standing still.
            '@media (prefers-reduced-motion: reduce)': {
              '& > span': { animation: 'none' },
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {characters.map((character, index) => (
          <Box
            // Characters repeat, so the index is the only stable identity
            // here — and the list is a fixed string that is never reordered.
            key={`${character}-${index}`}
            component="span"
            style={{ animationDelay: `${index * RIBBON_WAVE_STEP_MS}ms` }}
          >
            {character === ' ' ? ' ' : character}
          </Box>
        ))}
      </Box>
      <VisuallyHidden>{text}</VisuallyHidden>
    </>
  );
}
